import { BaggageType } from '@websky/graphql';
import { getStringsHasChanges } from '../../utils';
/**
 * Builds a stable, order-independent cache key for a baggage service.
 * Deduplicates and sorts segment IDs so that ["SEG1","SEG2"] and ["SEG2","SEG1"]
 * map to the same key.
 *
 * Pure function — safe to call multiple times with the same inputs.
 */
const getBaggageServiceKey = (rfisc, segmentIds) => {
    return `${rfisc}/${[...new Set(segmentIds)].sort().join('/')}`;
};
/**
 * Walks the baggage list and returns the single "winning" Baggage per
 * (rfics, segmentIdGroup) key — the one with the highest numeric ID.
 * Bags that are ineligible for this passenger/segment are excluded.
 */
export const buildActualBaggageMap = ({ baggage, segmentId, passengerId }) => {
    const actualBaggageMap = new Map();
    for (const bag of baggage) {
        if (!bag.canBeAdded || !bag.allowedPassengers.includes(passengerId)) {
            continue;
        }
        for (const segmentIdGroup of bag.segmentIds) {
            if (segmentId && !segmentIdGroup.includes(segmentId)) {
                continue;
            }
            const key = getBaggageServiceKey(bag.rfics, segmentIdGroup);
            const existBaggage = actualBaggageMap.get(key);
            if (!existBaggage || Number(existBaggage.id) < Number(bag.id)) {
                actualBaggageMap.set(key, bag);
            }
        }
    }
    return actualBaggageMap;
};
/**
 * Produces the full list of SelectedUpgrade items for every (bag, segmentIdGroup)
 * combination. Items are marked `disabledToAdd` when the bag is not the
 * authoritative ("actual") service for that key.
 */
export const getEmptyServicesItems = ({ baggage, segmentId, passengerId }) => {
    const actualBaggageMap = buildActualBaggageMap({ baggage, segmentId, passengerId });
    const items = [];
    for (const bag of baggage) {
        for (const segmentIdGroup of bag.segmentIds) {
            const key = getBaggageServiceKey(bag.rfics, segmentIdGroup);
            const actualService = actualBaggageMap.get(key);
            items.push({
                passengerId,
                count: 0,
                segmentIds: segmentIdGroup,
                serviceId: bag.id,
                disabledToAdd: (actualService === null || actualService === void 0 ? void 0 : actualService.id) !== bag.id
            });
        }
    }
    return items;
};
export const detectDifferentForReturnFlight = (segments, selectedServices) => {
    const servicesCount = new Map(), segmentsOfServices = new Map();
    for (const selectedService of selectedServices) {
        const serviceId = selectedService.serviceId, passengerId = selectedService.passengerId, key = `${passengerId}:${serviceId}`;
        if (!servicesCount.has(key)) {
            servicesCount.set(key, selectedService.count);
        }
        // detect case if different service count
        if (servicesCount.get(key) !== selectedService.count) {
            return true;
        }
        // collect all segments where service is active
        segmentsOfServices.set(key, [...(segmentsOfServices.get(key) || []), ...selectedService.segmentIds]);
    }
    for (const segmentOfService of segmentsOfServices.values()) {
        if (new Set(segmentOfService).size !== segments.length) {
            return true;
        }
    }
    return false;
};
export const joinServicesByRfics = (baggage) => {
    const servicesByRficsMap = new Map();
    baggage.forEach(baggage => {
        if (servicesByRficsMap.has(baggage.rfics)) {
            servicesByRficsMap.get(baggage.rfics).push(baggage);
        }
        else {
            servicesByRficsMap.set(baggage.rfics, [baggage]);
        }
    });
    return [...servicesByRficsMap.values()];
};
export const joinIncludedBaggageByRfics = (includedServices) => {
    const includedServiceMap = new Map();
    includedServices.forEach(service => {
        var _a;
        const key = `${service.confirmedCount}_${(_a = service.baggage) === null || _a === void 0 ? void 0 : _a.rfics}`;
        if (includedServiceMap.has(key)) {
            includedServiceMap.get(key).push(service);
        }
        else {
            includedServiceMap.set(key, [service]);
        }
    });
    const includedBaggageByRfics = [];
    includedServiceMap.forEach(items => {
        includedBaggageByRfics.push(items);
    });
    return includedBaggageByRfics;
};
export const filterIncludedBaggageByRfics = (baggageServices, includedBaggage, selectedBaggage) => {
    const baggageServiceMap = new Map(baggageServices.map(service => [service.id, service]));
    const selectedBaggageKeySet = new Set();
    selectedBaggage.forEach(baggage => {
        baggage.segmentIds.forEach(segmentId => {
            const gdsService = baggageServiceMap.get(baggage.serviceId);
            if (gdsService) {
                selectedBaggageKeySet.add(`${gdsService.rfics}_${baggage.passengerId}_${segmentId}`);
            }
        });
    });
    const filteredIncludedServices = [];
    includedBaggage.forEach(baggage => {
        if (baggage.baggage) {
            baggage.segmentIds.forEach(segmentId => {
                var _a;
                const isReplaced = (_a = baggage.baggage.isReplacedBy) === null || _a === void 0 ? void 0 : _a.some(rfics => selectedBaggageKeySet.has(`${rfics}_${baggage.passengerId}_${segmentId}`));
                if (!isReplaced) {
                    filteredIncludedServices.push(baggage);
                }
            });
        }
    });
    return filteredIncludedServices;
};
export const getRelatedSegments = (groups, segmentId = null) => {
    const result = new Set();
    if (segmentId === null) {
        groups.forEach(group => {
            group.forEach(segmentId => result.add(segmentId));
        });
    }
    else {
        groups.some(group => {
            if (group.includes(segmentId)) {
                group.forEach(segmentId => result.add(segmentId));
                return true;
            }
            return false;
        });
    }
    return result;
};
export const getMinPriceInBaggageGroup = (baggages) => {
    const price = {
        amount: baggages[0].price.amount,
        currency: baggages[0].price.currency
    };
    baggages.forEach(baggage => {
        if (baggage.price.amount < price.amount) {
            price.amount = baggage.price.amount;
        }
    });
    return price;
};
export const getActualPriceInBaggageGroup = (baggages, multipleSegments) => {
    const price = {
        amount: baggages[0].price.amount,
        currency: baggages[0].price.currency,
        withFrom: false
    };
    let actualBaggage = null;
    baggages.forEach(bag => {
        const lessActualPrice = !actualBaggage || bag.price.amount < (actualBaggage === null || actualBaggage === void 0 ? void 0 : actualBaggage.price.amount);
        if (isActualBaggage(bag, actualBaggage) && lessActualPrice) {
            actualBaggage = bag;
        }
        if (bag.price.amount < (actualBaggage === null || actualBaggage === void 0 ? void 0 : actualBaggage.price.amount) || bag.price.amount > (actualBaggage === null || actualBaggage === void 0 ? void 0 : actualBaggage.price.amount)) {
            price.withFrom = true;
        }
    });
    if (actualBaggage) {
        const segmentsCount = multipleSegments ? getRelatedSegments(actualBaggage.segmentIds).size : 1;
        price.amount = actualBaggage.price.amount * segmentsCount;
    }
    return price;
};
export const getSegmentsInBaggageGroup = (baggages) => {
    const segmentsSet = new Set();
    baggages.forEach(baggage => baggage.segmentIds.forEach(segments => segments.forEach(segment => segmentsSet.add(segment))));
    return segmentsSet;
};
export const getMinBaggageCount = (passenger, baggage, segmentId) => {
    var _a;
    const baggageIdSet = new Set(baggage.map(bag => bag.id));
    return (_a = passenger.services.find(service => {
        const isIncludeServiceId = baggageIdSet.has(service.serviceId);
        const isIncludeSegment = !segmentId || (segmentId && service.segmentIds.includes(segmentId));
        return isIncludeServiceId && isIncludeSegment;
    })) === null || _a === void 0 ? void 0 : _a.confirmedCount;
};
export const isActualBaggage = (currBaggage, actualBaggage, onlyAvailable = true) => {
    const actualBaggageById = !actualBaggage || +currBaggage.id > +actualBaggage.id;
    if (onlyAvailable) {
        return currBaggage.canBeAdded && actualBaggageById;
    }
    return actualBaggageById;
};
export const getActualBaggage = (baggage, segmentId, onlyAvailable = true) => {
    let actualBaggage = null;
    baggage.forEach(bag => {
        const segmentIdSet = new Set(bag.segmentIds.reduce((ids, segmentIds) => [...ids, ...segmentIds], []));
        const isIncludeSegment = !segmentId || (segmentId && segmentIdSet.has(segmentId));
        if (isActualBaggage(bag, actualBaggage, onlyAvailable) && isIncludeSegment) {
            actualBaggage = bag;
        }
    });
    return actualBaggage;
};
export const convertToBaggageAdapter = (item, canBeAdded = false) => {
    return item
        ? Object.assign(Object.assign({}, item), { id: item.serviceId, canBeAdded }) : null;
};
export const updateSelectedItems = (currCount, newCount, items, // empty items we can fill by current counter
selectedItems // items previously selected by user for current segment, rfics and passenger
) => {
    const dCount = newCount - currCount;
    // count > 1 can be booked by multiple services with same ids
    // Join services with same segment ids
    const selectedServicesBySegments = new Map();
    selectedItems.forEach(selected => {
        const key = selected.segmentIds.sort().join('-');
        selectedServicesBySegments.set(key, [...(selectedServicesBySegments.get(key) || []), selected]);
    });
    if (dCount > 0) {
        items.forEach(item => {
            var _a;
            if (!item.disabledToAdd) {
                const key = item.segmentIds.sort().join('-');
                const existServicesBySegment = (_a = selectedServicesBySegments.get(key)) !== null && _a !== void 0 ? _a : [];
                const existServiceById = existServicesBySegment.find(service => service.serviceId === item.serviceId);
                if (existServiceById) {
                    selectedServicesBySegments.set(key, existServicesBySegment.map(service => {
                        if (service.serviceId === item.serviceId) {
                            return Object.assign(Object.assign({}, service), { count: service.count + dCount });
                        }
                        return service;
                    }));
                }
                else {
                    const existServicesCount = existServicesBySegment.reduce((acc, service) => +acc + +service.count, 0);
                    selectedServicesBySegments.set(key, [
                        ...existServicesBySegment,
                        Object.assign(Object.assign({}, item), { count: newCount - existServicesCount })
                    ]);
                }
            }
        });
    }
    else {
        // Change the count only first service in segment and reset the rest
        selectedServicesBySegments.forEach((services, key) => {
            const servicesWithCount = services.filter(service => service.count > 0);
            selectedServicesBySegments.set(key, servicesWithCount.map((service, index) => {
                const isLastService = index === servicesWithCount.length - 1;
                if (isLastService) {
                    return Object.assign(Object.assign({}, service), { count: service.count + dCount });
                }
                return service;
            }));
        });
    }
    return [...selectedServicesBySegments.values()].reduce((acc, services) => [...acc, ...services], []);
};
export const isBaggageExcess = (baggage) => {
    return baggage.type === BaggageType.BaggageExcess;
};
export const isCanModifyServices = (services, passenger) => {
    return services.some(baggage => {
        if (!baggage.allowedPassengers.includes(passenger.id)) {
            return false;
        }
        return baggage.canBeAdded;
    });
};
export const getSelectedServicesKeys = (services) => {
    const selectedServicesSet = new Set();
    services.forEach(service => {
        if (service.count) {
            const key = [service.serviceId, service.passengerId, service.segmentIds.join(''), service.count].join('-');
            selectedServicesSet.add(key);
        }
    });
    return [...selectedServicesSet].sort();
};
export const getBaggageHasChanges = (initialServices, selectedServices) => {
    const initialKeys = getSelectedServicesKeys(initialServices);
    const selectedKeys = getSelectedServicesKeys(selectedServices);
    return getStringsHasChanges(initialKeys, selectedKeys);
};
export const getSelectedServicesWithConfirmedCount = (selectedServices, passengers) => {
    return selectedServices.map(selected => {
        var _a, _b, _c, _d;
        const confirmedCount = (_d = (_c = (_b = (_a = passengers
            .find(passenger => passenger.id === selected.passengerId)) === null || _a === void 0 ? void 0 : _a.services) === null || _b === void 0 ? void 0 : _b.find(service => service.serviceId === selected.serviceId && service.segmentIds[0] === selected.segmentIds[0])) === null || _c === void 0 ? void 0 : _c.confirmedCount) !== null && _d !== void 0 ? _d : 0;
        return Object.assign(Object.assign({}, selected), { confirmedCount });
    });
};
